import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import {
  clearCampaignDetails,
  fetchCampaignDetailsAction,
} from "@/modules/stochastic/features/CampaignManagement/slices/CampaignDetailsSlice";
import { updateCampaignAction } from "@/modules/stochastic/features/CampaignManagement/slices/campaignSlice";
import { useNotification } from "@/context/NotificationContext";
import { FilePickerSelectedFile } from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import { parsePhoneNumber } from "react-phone-number-input/input";
import { CampaignOwnerEmployee } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignOwnerEmployeesLookup/types";

const PHONE_NUMBER_PATTERN = /^(?:\+[1-9]\d{1,14}|\d{3}-\d{3}-\d{4})$/;

const CAMPAIGNS_PATH = "/lead-generation/campaigns";

interface EditCampaignFormData {
  name: string;
  description: string;
  phoneNumber: string;
  licenseNumber: string;
  permitNumber: string;
  ownerEmployee: CampaignOwnerEmployee | null;
}

interface CreativeAsset {
  uuid: string | null;
  fileName: string;
  displayUrl: string;
}

const EMPTY_CREATIVE_ASSET: CreativeAsset = {
  uuid: null,
  fileName: "",
  displayUrl: "",
};

const EMPTY_FORM_DATA: EditCampaignFormData = {
  name: "",
  description: "",
  phoneNumber: "",
  licenseNumber: "",
  permitNumber: "",
  ownerEmployee: null,
};

const toNullableValue = (value: string): string | null => value.trim() || null;

/**
 * The details endpoint sends an empty array, not null, when a campaign has no
 * owner — and [] is truthy, so it cannot be used as the value directly.
 */
const toOwnerEmployee = (
  value: CampaignOwnerEmployee | null | undefined,
): CampaignOwnerEmployee | null => (value?.id ? value : null);

/**
 * PhoneInput renders whatever string it is given without validating it, and does not
 * fire onChange on mount. A stored value in any other shape would therefore look fine
 * on screen but fail validation on submit, blocking every other field on the form.
 * Normalizing here means the first save quietly repairs the record. Anything
 * unparseable is left as-is so validation surfaces it rather than silently dropping it.
 */
const toE164PhoneNumber = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return parsePhoneNumber(value, "US")?.number ?? value;
};

export const useEditCampaign = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const { campaignDetails, loading, error } = useSelector(
    (state: RootState) => state.campaignDetails,
  );
  const isSaving = useSelector(
    (state: RootState) => state.campaign.loadingUpdate,
  );

  const [formData, setFormData] =
    useState<EditCampaignFormData>(EMPTY_FORM_DATA);
  const [creativeAssetFront, setCreativeAssetFront] =
    useState<CreativeAsset>(EMPTY_CREATIVE_ASSET);
  const [creativeAssetBack, setCreativeAssetBack] =
    useState<CreativeAsset>(EMPTY_CREATIVE_ASSET);

  const [isFrontPickerOpen, setIsFrontPickerOpen] = useState(false);
  const [isBackPickerOpen, setIsBackPickerOpen] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  const detailsPath = `/lead-generation/campaigns/${campaignId}/view`;

  useEffect(() => {
    if (campaignId) {
      dispatch(fetchCampaignDetailsAction({ campaignId }));
    }

    return () => {
      dispatch(clearCampaignDetails());
    };
  }, [campaignId, dispatch]);

  useEffect(() => {
    if (!campaignDetails) {
      return;
    }

    setFormData({
      name: campaignDetails.name ?? "",
      description: campaignDetails.description ?? "",
      phoneNumber: toE164PhoneNumber(campaignDetails.phoneNumber),
      licenseNumber: campaignDetails.licenseNumber ?? "",
      permitNumber: campaignDetails.permitNumber ?? "",
      ownerEmployee: toOwnerEmployee(campaignDetails.ownerEmployee),
    });

    const front = campaignDetails.creativeAssets?.front;
    const back = campaignDetails.creativeAssets?.back;

    setCreativeAssetFront({
      uuid: front?.uuid ?? null,
      fileName: front?.name ?? "",
      displayUrl: front?.publicUrl ?? "",
    });
    setCreativeAssetBack({
      uuid: back?.uuid ?? null,
      fileName: back?.name ?? "",
      displayUrl: back?.publicUrl ?? "",
    });
  }, [campaignDetails]);

  const handleInputChange = useCallback(
    (field: keyof EditCampaignFormData, value: string) => {
      setFormData((previous) => ({ ...previous, [field]: value }));
    },
    [],
  );

  const handleOwnerEmployeeChange = useCallback(
    (ownerEmployee: CampaignOwnerEmployee | null) => {
      setFormData((previous) => ({ ...previous, ownerEmployee }));
    },
    [],
  );

  const openFrontPicker = useCallback(() => setIsFrontPickerOpen(true), []);
  const closeFrontPicker = useCallback(() => setIsFrontPickerOpen(false), []);
  const openBackPicker = useCallback(() => setIsBackPickerOpen(true), []);
  const closeBackPicker = useCallback(() => setIsBackPickerOpen(false), []);

  const handleFrontSelected = useCallback((files: FilePickerSelectedFile[]) => {
    if (files.length === 0) {
      return;
    }

    const file = files[0];
    setCreativeAssetFront({
      uuid: file.fileUuid,
      fileName: file.originalFilename || file.name,
      displayUrl: file.publicUrl,
    });
    setIsFrontPickerOpen(false);
  }, []);

  const handleBackSelected = useCallback((files: FilePickerSelectedFile[]) => {
    if (files.length === 0) {
      return;
    }

    const file = files[0];
    setCreativeAssetBack({
      uuid: file.fileUuid,
      fileName: file.originalFilename || file.name,
      displayUrl: file.publicUrl,
    });
    setIsBackPickerOpen(false);
  }, []);

  const handleRemoveFront = useCallback(
    () => setCreativeAssetFront(EMPTY_CREATIVE_ASSET),
    [],
  );
  const handleRemoveBack = useCallback(
    () => setCreativeAssetBack(EMPTY_CREATIVE_ASSET),
    [],
  );

  const handleCancel = useCallback(() => navigate(CAMPAIGNS_PATH), [navigate]);

  const handleBack = useCallback(() => navigate(CAMPAIGNS_PATH), [navigate]);

  const validate = useCallback((): boolean => {
    const trimmedName = formData.name.trim();
    const trimmedPhoneNumber = formData.phoneNumber.trim();

    const nextNameError = trimmedName ? null : "Campaign name is required.";
    const nextPhoneNumberError =
      !trimmedPhoneNumber || PHONE_NUMBER_PATTERN.test(trimmedPhoneNumber)
        ? null
        : "Enter a valid phone number.";

    setNameError(nextNameError);
    setPhoneNumberError(nextPhoneNumberError);

    return !nextNameError && !nextPhoneNumberError;
  }, [formData.name, formData.phoneNumber]);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      if (!campaignId || !validate()) {
        return;
      }

      dispatch(
        updateCampaignAction(
          Number(campaignId),
          {
            name: formData.name.trim(),
            description: toNullableValue(formData.description),
            phoneNumber: toNullableValue(formData.phoneNumber),
            licenseNumber: toNullableValue(formData.licenseNumber),
            permitNumber: toNullableValue(formData.permitNumber),
            creativeAssetFrontUuid: creativeAssetFront.uuid,
            creativeAssetBackUuid: creativeAssetBack.uuid,
            ownerEmployeeId: formData.ownerEmployee?.id ?? null,
          },
          () => {
            showNotification(
              "Campaign updated",
              "Your changes have been saved.",
              "success",
            );
            navigate(detailsPath);
          },
        ),
      );
    },
    [
      campaignId,
      validate,
      formData,
      creativeAssetFront.uuid,
      creativeAssetBack.uuid,
      dispatch,
      showNotification,
      navigate,
      detailsPath,
    ],
  );

  return {
    campaignDetails,
    loading,
    error,
    isSaving,
    formData,
    nameError,
    phoneNumberError,
    handleInputChange,
    handleOwnerEmployeeChange,
    handleSubmit,
    handleCancel,
    handleBack,
    creativeAssetFront,
    creativeAssetBack,
    isFrontPickerOpen,
    isBackPickerOpen,
    openFrontPicker,
    closeFrontPicker,
    openBackPicker,
    closeBackPicker,
    handleFrontSelected,
    handleBackSelected,
    handleRemoveFront,
    handleRemoveBack,
  };
};
